/** Keyboard shortcuts cheat sheet: one sheet mounted in the shell, listing the registry and opened by ?, Cmd/Ctrl+/ or the 'shortcuts:open' window event. */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../../../shared/ui/sheet';
import { currentMainRailShortcutModifier } from '../../../platform/navigation/useMainRailShortcuts';
import { SHORTCUT_GROUPS, buildShortcutRows } from '../../../platform/shortcuts/shortcutRegistry';
import { SHORTCUTS_OPEN_EVENT } from '../../../platform/shortcuts/useGlobalShortcuts';
import { isApplePlatform } from '../../../utils/keyboardModifier';

export { SHORTCUTS_OPEN_EVENT };

function Keycap({ children }: { children: string }) {
  return (
    <kbd className="min-w-6 rounded-md border border-border bg-muted px-1.5 py-0.5 text-center font-apple text-[length:var(--text-label)] text-foreground">
      {children}
    </kbd>
  );
}

export function ShortcutsSheet({ canAddProject = true }: { canAddProject?: boolean }) {
  const [open, setOpen] = useState(false);
  const rows = useMemo(
    () => buildShortcutRows({ mac: isApplePlatform(), railModifier: currentMainRailShortcutModifier() }, { canAddProject }),
    [canAddProject],
  );

  useEffect(() => {
    const onOpen = (event: Event) => {
      const toggle = (event as CustomEvent<{ toggle?: boolean } | null>).detail?.toggle === true;
      setOpen((current) => (toggle ? !current : true));
    };
    window.addEventListener(SHORTCUTS_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(SHORTCUTS_OPEN_EVENT, onOpen);
  }, []);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Keyboard shortcuts</SheetTitle>
          <SheetDescription>Cmd and Ctrl both work. Nothing fires while you are typing in a field.</SheetDescription>
        </SheetHeader>
        <div className="flex flex-col gap-5 px-4 pb-6">
          {SHORTCUT_GROUPS.map((group) => {
            const groupRows = rows.filter((row) => row.group === group.id);
            if (groupRows.length === 0) return null;
            return (
              <section key={group.id} aria-labelledby={`shortcuts-${group.id}`}>
                <h3 id={`shortcuts-${group.id}`} className="pb-1 text-[length:var(--text-label)] font-semibold text-muted-foreground">
                  {group.title}
                </h3>
                <ul className="flex flex-col">
                  {groupRows.map((row) => (
                    <li key={row.id} className="flex items-center justify-between gap-4 border-b border-border py-2.5 last:border-b-0">
                      <span className="text-[length:var(--text-menu)] text-foreground">{row.label}</span>
                      <span className="flex shrink-0 items-center gap-1 text-[length:var(--text-label)] text-muted-foreground">
                        {row.keys.map((combo, comboIndex) => (
                          <Fragment key={combo.join('+')}>
                            {comboIndex > 0 && <span className="px-0.5">or</span>}
                            {combo.map((key, keyIndex) => (
                              <Fragment key={key}>
                                {row.sequence && keyIndex > 0 && <span className="px-0.5">then</span>}
                                <Keycap>{key}</Keycap>
                              </Fragment>
                            ))}
                          </Fragment>
                        ))}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
}
